import React, { useState } from 'react';

const AdminBankingSettings: React.FC = () => {
  const [values, setValues] = useState({
    dailyTransactionLimit: 10000,
    perTransactionLimit: 5000,
    kycLevel1Limit: 1000,
    kycLevel2Limit: 5000,
    kycLevel3Limit: 50000,
    enableSanctionsScreening: true,
    sanctionsScreeningProvider: 'sumsub',
    riskScoreThreshold: 70,
    enableKYT: true,
    kytProvider: 'sumsub',
    complianceReviewRequired: true,
    autoApprovalThreshold: 1000
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const handleInputChange = (field: string, value: string | boolean | number) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveMessage('Banking settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '32px auto', 
      padding: '32px',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ 
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #f3f4f6'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🏦 Banking Configuration
          </span>
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#6b7280', 
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Configure transaction limits, compliance settings, and risk management parameters
        </p>
      </div>

      {/* Transaction Limits */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          💰 Transaction Limits
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Daily Transaction Limit ($)
            </label>
            <input
              type="number"
              min={100}
              max={1000000}
              value={values.dailyTransactionLimit}
              onChange={e => handleInputChange('dailyTransactionLimit', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Per Transaction Limit ($)
            </label>
            <input
              type="number"
              min={10}
              max={100000}
              value={values.perTransactionLimit}
              onChange={e => handleInputChange('perTransactionLimit', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
          gap: '16px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              KYC Level 1 Limit ($)
            </label>
            <input
              type="number"
              min={10}
              max={5000}
              value={values.kycLevel1Limit}
              onChange={e => handleInputChange('kycLevel1Limit', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              KYC Level 2 Limit ($)
            </label>
            <input
              type="number"
              min={100}
              max={25000}
              value={values.kycLevel2Limit}
              onChange={e => handleInputChange('kycLevel2Limit', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              KYC Level 3 Limit ($)
            </label>
            <input
              type="number"
              min={1000}
              max={100000}
              value={values.kycLevel3Limit}
              onChange={e => handleInputChange('kycLevel3Limit', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '2px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>
      </div>

      {/* Compliance Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '12px',
        border: '1px solid #fbbf24'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🛡️ Compliance Settings
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Sanctions Screening Provider
            </label>
            <select
              value={values.sanctionsScreeningProvider}
              onChange={e => handleInputChange('sanctionsScreeningProvider', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box',
                background: '#ffffff'
              }}
            >
              <option value="sumsub">Sumsub</option>
              <option value="jumio">Jumio</option>
              <option value="onfido">Onfido</option>
              <option value="custom">Custom Provider</option>
            </select>
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              Risk Score Threshold
            </label>
            <input
              type="number"
              min={0}
              max={100}
              value={values.riskScoreThreshold}
              onChange={e => handleInputChange('riskScoreThreshold', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <div style={{ 
              fontSize: '13px', 
              color: '#6b7280', 
              marginTop: '4px'
            }}>
              Transactions above this score require manual review
            </div>
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <input
              type="checkbox"
              checked={values.enableSanctionsScreening}
              onChange={e => handleInputChange('enableSanctionsScreening', e.target.checked)}
              style={{ 
                width: '18px',
                height: '18px',
                accentColor: '#f59e0b'
              }}
            />
            <label style={{ 
              fontWeight: '500',
              color: '#374151',
              fontSize: '14px',
              margin: 0,
              cursor: 'pointer'
            }}>
              Enable Sanctions Screening
            </label>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <input
              type="checkbox"
              checked={values.enableKYT}
              onChange={e => handleInputChange('enableKYT', e.target.checked)}
              style={{ 
                width: '18px',
                height: '18px',
                accentColor: '#f59e0b'
              }}
            />
            <label style={{ 
              fontWeight: '500',
              color: '#374151',
              fontSize: '14px',
              margin: 0,
              cursor: 'pointer'
            }}>
              Enable KYT (Know Your Transaction)
            </label>
          </div>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            background: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <input
              type="checkbox"
              checked={values.complianceReviewRequired}
              onChange={e => handleInputChange('complianceReviewRequired', e.target.checked)}
              style={{ 
                width: '18px',
                height: '18px',
                accentColor: '#f59e0b'
              }}
            />
            <label style={{ 
              fontWeight: '500',
              color: '#374151',
              fontSize: '14px',
              margin: 0,
              cursor: 'pointer'
            }}>
              Require Compliance Review
            </label>
          </div>
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
          }}>
            Auto-Approval Threshold ($)
          </label>
          <input
            type="number"
            min={0}
            max={10000}
            value={values.autoApprovalThreshold}
            onChange={e => handleInputChange('autoApprovalThreshold', Number(e.target.value))}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ 
            fontSize: '13px', 
            color: '#6b7280', 
            marginTop: '4px'
          }}>
            Transactions below this amount are automatically approved
          </div>
        </div>
      </div>

      {/* KYT Configuration */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: '12px',
        border: '1px solid #d1fae5'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🔍 KYT Configuration
        </h3>
        
        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
          }}>
            KYT Provider
          </label>
          <select
            value={values.kytProvider}
            onChange={e => handleInputChange('kytProvider', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box',
              background: '#ffffff'
            }}
          >
            <option value="sumsub">Sumsub</option>
            <option value="chainalysis">Chainalysis</option>
            <option value="elliptic">Elliptic</option>
            <option value="custom">Custom Provider</option>
          </select>
          <div style={{ 
            fontSize: '13px', 
            color: '#6b7280', 
            marginTop: '4px'
          }}>
            Provider for Know Your Transaction analysis
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '16px 32px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isSaving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save Banking Settings
            </>
          )}
        </button>
        
        {saveMessage && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: saveMessage.includes('successfully') ? '#065f46' : '#991b1b',
            background: saveMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${saveMessage.includes('successfully') ? '#a7f3d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {saveMessage.includes('successfully') ? '✅' : '❌'} {saveMessage}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminBankingSettings;
